import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { UserProtectedRoute, AdminProtectedRoute, BPProtectedRoute, POSProtectedRoute, PublicRoute, AnyAuthRoute } from './components/ProtectedRoute';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Public Pages
import LandingPage from './pages/LandingPage';
import AboutUs from './pages/AboutUs';
import Test from './pages/Test';

// import SEO from './pages/SEO';                         // hidden for gateway approval
// import PaidAdvertisement from './pages/PaidAdvertisement'; // hidden for gateway approval
// import SocialMedia from './pages/SocialMedia';             // hidden for gateway approval
// import ContentMarketing from './pages/ContentMarketing';   // hidden for gateway approval
import SoftwareDevelopment from './pages/SoftwareDevelopment';

import Pricing from './pages/Pricing';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsConditions from './pages/TermsConditions';
import ReturnRefundPolicy from './pages/ReturnRefundPolicy';
import FAQ from './pages/FAQ';
import ContactUs from './pages/ContactUs';

// Auth
import Login from './components/Auth/Login';
import Register from './components/Auth/Register';
import ForgotPassword from './components/Auth/ForgotPassword';

// User Panel
import UserDashboard from './components/User/UserDashboard';
import UserProfile from './components/User/UserProfile';
import UserWallet from './components/User/UserWallet';
import UserTransactions from './components/User/UserTransactions';
import UserWithdrawal from './components/User/UserWithdrawal';
import UserProductCatalogue from './components/User/UserProductCatalogue';
import UserSupport from './components/User/UserSupport';
import APIs from './components/User/APIs';
import PendingApproval from './components/User/PendingApproval';
import UserKYC from './components/User/UserKYC';
import UserApiIntegration from './components/User/UserApiIntegration';
import UserSettlements from './components/User/UserSettlements';
import ShopifyIntegration from './components/User/ShopifyIntegration';
import WordPressIntegration from './components/User/WordPressIntegration';
import UserPaymentLinks from './components/User/UserPaymentLinks';
import PaymentLinkCheckout from './pages/PaymentLinkCheckout';

// Admin Panel
import AdminDashboard from './components/Admin/AdminDashboard';
import AdminUsers from './components/Admin/Users/AdminUsers';
import AdminUserDetails from './components/Admin/Users/AdminUserDetails';
import AdminTransactions from './components/Admin/Transactions/AdminTransactions';
import AdminTransactionDetails from './components/Admin/Transactions/AdminTransactionDetails';
import AdminWithdrawals from './components/Admin/Withdrawals/AdminWithdrawals';
import AdminWithdrawalDetails from './components/Admin/Withdrawals/AdminWithdrawalDetails';
import AdminSupport from './components/Admin/Support/AdminSupport';
import AdminTicketDetails from './components/Admin/Support/AdminTicketDetails';
import AdminProfile from './components/Admin/Settings/AdminProfile';
import AdminCommission from './components/Admin/Settings/AdminCommission';
import AdminSiteSettings from './components/Admin/Settings/AdminSiteSettings';
import AdminMerchants from './components/Admin/Merchants/AdminMerchants';
import AdminMerchantDetails from './components/Admin/Merchants/AdminMerchantDetails';
import AdminMerchantTabs from './components/Admin/Merchants/AdminMerchantTabs';
import AdminSettlements from './components/Admin/Settlements/AdminSettlements';
import AdminWebhookLogs from './components/Admin/Webhooks/AdminWebhookLogs';
import AdminGatewayManage from './components/Admin/Gateway/AdminGatewayManage';
import AdminKYC from './components/Admin/KYC/AdminKYC';
import AdminKYCDetail from './components/Admin/KYC/AdminKYCDetail';
import AdminRoles from './components/Admin/Roles/AdminRoles';
import AdminBusinessPartners from './components/Admin/BusinessPartner/AdminBusinessPartners';
import AdminSMSCampaign from './components/Admin/Campaign/AdminSMSCampaign';
import AdminWhatsAppCampaign from './components/Admin/Campaign/AdminWhatsAppCampaign';

// POS Panel
import POSDashboard    from './components/POS/POSDashboard';
import POSCollect      from './components/POS/POSCollect';
import POSTransactions from './components/POS/POSTransactions';

// Business Partner Panel
import BPDashboard    from './components/BusinessPartner/BPDashboard';
import BPMerchants    from './components/BusinessPartner/BPMerchants';
import BPTransactions from './components/BusinessPartner/BPTransactions';
import BPKycLinks     from './components/BusinessPartner/BPKycLinks';
import BPSettlements  from './components/BusinessPartner/BPSettlements';

import SEOManager from './components/SEOManager';
import Checkout from './pages/Checkout/Checkout';
import CheckoutSuccess from './pages/Checkout/CheckoutSuccess';
import PanelLayout from './components/Panel/PanelLayout';

const Admin = (title, El, breadcrumb) => (
  <AdminProtectedRoute>
    <PanelLayout role="admin" title={title} breadcrumb={breadcrumb}>{El}</PanelLayout>
  </AdminProtectedRoute>
);

const Merchant = (title, El, breadcrumb) => (
  <UserProtectedRoute>
    <PanelLayout role="user" title={title} breadcrumb={breadcrumb}>{El}</PanelLayout>
  </UserProtectedRoute>
);

function App() {
  return (
    <AuthProvider>
      <Router>
        <SEOManager />
        <Routes>
          {/* Public */}
          <Route path="/"                              element={<LandingPage />} />
          <Route path="/about-us"                      element={<AboutUs />} />
          <Route path="/test"                          element={<Test />} />
          {/* <Route path="/services/seo"                element={<SEO />} /> */}
          {/* <Route path="/services/paid-advertisement" element={<PaidAdvertisement />} /> */}
          {/* <Route path="/services/social-media"       element={<SocialMedia />} /> */}
          {/* <Route path="/services/content-marketing"  element={<ContentMarketing />} /> */}
          <Route path="/services/software-development" element={<SoftwareDevelopment />} />
          <Route path="/pricing"                       element={<Pricing />} />
          <Route path="/privacy-policy"                element={<PrivacyPolicy />} />
          <Route path="/terms-conditions"              element={<TermsConditions />} />
          <Route path="/return-refund-policy"          element={<ReturnRefundPolicy />} />
          <Route path="/faq"                           element={<FAQ />} />
          <Route path="/contact-us"                    element={<ContactUs />} />
          <Route path="/checkout/:plan"                element={<Checkout />} />
          <Route path="/checkout/:plan/success"        element={<CheckoutSuccess />} />
          <Route path="/link/:token"                   element={<PaymentLinkCheckout />} />

          {/* Auth */}
          <Route path="/login"           element={<PublicRoute><Login /></PublicRoute>} />
          <Route path="/register"        element={<PublicRoute><Register /></PublicRoute>} />
          <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />

          {/* KYC & Pending — auth required, no PanelLayout */}
          <Route path="/user/kyc"     element={<AnyAuthRoute><UserKYC /></AnyAuthRoute>} />
          <Route path="/user/pending" element={<AnyAuthRoute><PendingApproval /></AnyAuthRoute>} />

          {/* Merchant Panel (active accounts only) */}
          <Route path="/user/dashboard"     element={Merchant('Dashboard',         <UserDashboard />)} />
          <Route path="/user/profile"       element={Merchant('Profile',           <UserProfile />)} />
          <Route path="/user/wallet"        element={Merchant('Wallet',            <UserWallet />)} />
          <Route path="/user/transactions"  element={Merchant('Transactions',      <UserTransactions />)} />
          <Route path="/user/withdrawal"    element={Merchant('Withdrawals',       <UserWithdrawal />)} />
          <Route path="/user/catalogue"     element={Merchant('Product Catalogue', <UserProductCatalogue />)} />
          <Route path="/user/support"       element={Merchant('Support',           <UserSupport />)} />
          <Route path="/user/APIs"          element={Merchant('API Integration',   <APIs />)} />
          <Route path="/user/integration"   element={Merchant('API Integration',   <UserApiIntegration />)} />
          <Route path="/user/settlements"   element={Merchant('Settlements',       <UserSettlements />)} />
          <Route path="/user/shopify"         element={Merchant('Shopify Integration',    <ShopifyIntegration />)} />
          <Route path="/user/wordpress"     element={Merchant('WordPress Integration', <WordPressIntegration />)} />
          <Route path="/user/payment-links" element={Merchant('Payment Links',         <UserPaymentLinks />)} />

          {/* Admin Panel */}
          <Route path="/admin/dashboard"                  element={Admin('Dashboard',          <AdminDashboard />)} />
          <Route path="/admin/users"                      element={Admin('All Users',          <AdminUsers />)} />
          <Route path="/admin/users/:userId"              element={Admin('User Details',       <AdminUserDetails />, 'Users')} />
          <Route path="/admin/transactions"               element={Admin('Transactions',       <AdminTransactions />)} />
          <Route path="/admin/transactions/:transactionId" element={Admin('Transaction',       <AdminTransactionDetails />, 'Transactions')} />
          <Route path="/admin/withdrawals"                element={Admin('Withdrawals',        <AdminWithdrawals />)} />
          <Route path="/admin/withdrawals/:withdrawalId"  element={Admin('Withdrawal Details', <AdminWithdrawalDetails />, 'Withdrawals')} />
          <Route path="/admin/support"                    element={Admin('Support Tickets',    <AdminSupport />)} />
          <Route path="/admin/support/:ticketId"          element={Admin('Ticket Details',     <AdminTicketDetails />, 'Support')} />
          <Route path="/admin/profile"                    element={Admin('Admin Profile',      <AdminProfile />, 'Settings')} />
          <Route path="/admin/commission"                 element={Admin('Commission',         <AdminCommission />, 'Settings')} />
          <Route path="/admin/site-settings"              element={Admin('Site Settings',      <AdminSiteSettings />, 'Settings')} />
          <Route path="/admin/merchants"                  element={Admin('Merchants',          <AdminMerchants />)} />
          <Route path="/admin/merchants/:id"              element={Admin('Merchant Details',   <AdminMerchantDetails />, 'Merchants')} />
          <Route path="/admin/merchant-tabs"              element={Admin('Merchant Tabs',      <AdminMerchantTabs />, 'Settings')} />
          <Route path="/admin/settlements"                element={Admin('Settlements',        <AdminSettlements />)} />
          <Route path="/admin/webhook-logs"               element={Admin('Webhook Logs',       <AdminWebhookLogs />)} />
          <Route path="/admin/gateway-manage"             element={<AdminProtectedRoute><AdminGatewayManage /></AdminProtectedRoute>} />
          <Route path="/admin/kyc"                        element={Admin('KYC Forms',          <AdminKYC />)} />
          <Route path="/admin/kyc/:id"                    element={Admin('KYC Detail',         <AdminKYCDetail />, 'KYC Forms')} />
          <Route path="/admin/roles"                      element={Admin('Roles & Permissions', <AdminRoles />, 'Settings')} />
          <Route path="/admin/business-partners"          element={Admin('Business Partners', <AdminBusinessPartners />, '')} />
          <Route path="/admin/campaign/sms"              element={Admin('SMS Campaign',       <AdminSMSCampaign />,       'Campaigns')} />
          <Route path="/admin/campaign/whatsapp"         element={Admin('WhatsApp Campaign',  <AdminWhatsAppCampaign />,  'Campaigns')} />

          {/* ── POS Panel ── */}
          <Route path="/pos/dashboard"    element={<POSProtectedRoute><POSDashboard /></POSProtectedRoute>} />
          <Route path="/pos/collect"      element={<POSProtectedRoute><POSCollect /></POSProtectedRoute>} />
          <Route path="/pos/transactions" element={<POSProtectedRoute><POSTransactions /></POSProtectedRoute>} />

          {/* ── Business Partner Panel ── */}
          <Route path="/bp/dashboard"    element={<BPProtectedRoute><BPDashboard /></BPProtectedRoute>} />
          <Route path="/bp/merchants"    element={<BPProtectedRoute><BPMerchants /></BPProtectedRoute>} />
          <Route path="/bp/transactions" element={<BPProtectedRoute><BPTransactions /></BPProtectedRoute>} />
          <Route path="/bp/kyc-links"    element={<BPProtectedRoute><BPKycLinks /></BPProtectedRoute>} />
          <Route path="/bp/settlements"  element={<BPProtectedRoute><BPSettlements /></BPProtectedRoute>} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>

        <ToastContainer
          position="top-right" autoClose={3000} hideProgressBar={false}
          newestOnTop={false} closeOnClick rtl={false}
          pauseOnFocusLoss draggable pauseOnHover theme="light"
        />
      </Router>
    </AuthProvider>
  );
}

export default App;
